/**
 * TaskCategoryCard — the area of work a task belongs to (CAT1).
 *
 * Its own sidebar card rather than one more row in "Details", because
 * this is the field the reports are grouped by: it decides which bucket
 * an hour lands in, so it gets the weight of a heading, an icon and the
 * one-line explanation of what belongs in it.
 *
 * A task without a category is normal, not an error. The field was added
 * after the fact and existing rows were deliberately NOT backfilled —
 * guessing "administration" for everything old would put invented data
 * into exactly the report this field exists to make honest. So the empty
 * state says "Nicht zugewiesen" and points at the edit button; it does
 * not scold.
 *
 * Also renders for work reports (same field, same table), which is why
 * the prop is a bare code rather than a task.
 */
import React from "react";

import { useCategoryDisplay } from "../activityCategories/ActivityCategoryChip";

export default function TaskCategoryCard({ code, headingId }) {
  // RESP2d: label, icon and colour come from the organisation's own
  // catalog, so a rename in the settings page reaches this card without
  // touching the task row. `state` distinguishes "no category" from "a
  // category the catalog could not resolve" — the second must never
  // render the raw key.
  const { state, label, description, icon: Icon, colorToken, isInactive } =
    useCategoryDisplay(code);
  const known = state === "known";

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#13161f]"
    >
      <h2
        id={headingId}
        className="m-0 mb-3 text-[12px] font-semibold uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]"
      >
        Tätigkeitsbereich
      </h2>

      {known ? (
        <div className="flex items-start gap-3">
          <span
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border ${colorToken}`}
            aria-hidden="true"
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="m-0 text-[13px] font-semibold text-foreground dark:text-[#c5cbe0]">
              {label}
            </p>
            {description ? (
              <p className="m-0 mt-1 text-[11px] leading-relaxed text-muted-foreground dark:text-[#4a5070]">
                {description}
              </p>
            ) : null}
            {isInactive ? (
              // Not a warning — the task is fine. The area simply is no
              // longer offered for new work, and saying so beats leaving
              // someone to wonder why they cannot pick it again.
              <p className="m-0 mt-1 text-[11px] font-medium text-muted-foreground dark:text-[#4a5070]">
                Dieser Bereich ist deaktiviert.
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <p className="m-0 text-[13px] font-medium text-muted-foreground dark:text-[#4a5070]">
            {label}
          </p>
          <p className="m-0 mt-1 text-[11px] leading-relaxed text-muted-foreground dark:text-[#4a5070]">
            Über „Bearbeiten" lässt sich ein Bereich nachtragen.
          </p>
        </>
      )}
    </section>
  );
}
